import { useMedia } from "react-use";
import { useRouter } from "next/router";
import debounce from "lodash.debounce";
import { HomePage, PageDocument, PageQuery, Resource, useSearchResourcesQuery } from "graphql/generated-types";
import PageContainer from "@/layouts/PageContainer";
import PageInner from "@/layouts/PageInner";
import Image from "next/image";

import { Helmet, addApolloState, initializeApollo, renderMeta } from "@neonjungle/headless";
import { PageProps } from "@/lib/types";
import { GetStaticProps } from "next";
import { useEffect, useRef } from "react";

const Resource = ({ resource: { title, resourceType, image, url } }: { resource: Resource }) => {
	return (
		<a className="flex gap-5 lg:flex-col" href={url}>
			<Image
				className="w-24 rounded lg:mb-7 lg:w-full"
				src={image?.rendition?.url!}
				width={image?.rendition?.width}
				height={image?.rendition?.height}
				alt={image?.rendition?.image.title!}
			/>
			<div>
				<div className="text-xxs text-blue-lightest mb-2 font-bold uppercase leading-4 lg:text-sm">{resourceType}</div>
				<div className="mb-10 text-[22px] leading-tight lg:text-lg">{title}</div>
			</div>
		</a>
	);
};

export default function Search(props: { data: PageQuery }) {
	const router = useRouter();
	const { data, loading } = useSearchResourcesQuery({
		variables: {
			query: router.query.q ? (router.query.q as string) : "",
		},
	});
	const inputRef = useRef<HTMLInputElement>(null);
	let items: Resource[] = loading ? [] : (data?.resources as Resource[]);

	const handleQueryChange = debounce((query?: string) => {
		let route = router.route;
		router.push(`${route}?q=${query}`, undefined, { shallow: true });
	}, 750);

	// SEO (no page so we'll use home page but overwrite title)
	const site = props.data.site!;
	const home = structuredClone(props.data!.site!.page) as HomePage;
	home.seoTitle = "Search";

	useEffect(() => {
		inputRef.current?.focus();
	});

	return (
		<PageContainer pageType="SearchPage">
			<Helmet>{renderMeta(home, site)}</Helmet>

			<PageInner>
				<div className="mt-20 lg:mt-56">
					<input
						onChange={(e) => handleQueryChange(e.target.value)}
						className="caret-caret placeholder:text-blue cursor- mb-10 w-full text-[22px] outline-none lg:mb-24 lg:text-2xl"
						type="text"
						placeholder="  Type here to search"
						defaultValue={router.query.q}
						ref={inputRef}
					></input>
					{loading ? (
						<div>Loading</div>
					) : items.length == 0 ? (
						<div>No results</div>
					) : (
						<>
							<div className="text-blue-lightest mb-10 font-bold lg:mb-16">{items.length} SEARCH RESULTS</div>
							<div className="grid gap-y-6 lg:grid-cols-4 lg:gap-x-10 lg:gap-y-20">
								{items?.map((resource, idx) => <Resource key={idx} resource={resource}></Resource>)}
							</div>
						</>
					)}
				</div>
			</PageInner>
		</PageContainer>
	);
}

export const getStaticProps: GetStaticProps = async () => {
	const apolloClient = initializeApollo();

	const { data } = await apolloClient.query({
		query: PageDocument,
		variables: {
			hostname: process.env.HOSTNAME,
			urlPath: "/",
		},
	});

	const pageProps = addApolloState(apolloClient, {
		props: {
			data,
		},
	}) as PageProps;

	pageProps.revalidate = 10;

	return pageProps;
};
